import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  // MUI's `md` is 900px, so a portrait tablet (768-834) fell all the way back
  // to the phone layout and rendered a single full-width column — long lines of
  // text with the left rail hidden and most of the screen empty. `tablet` is
  // the width at which the two-column funnel layout starts to fit.
  breakpoints: {
    values: { xs: 0, sm: 600, tablet: 768, md: 900, lg: 1200, xl: 1536 },
  },
  palette: {
    main: {
      primary: "#FBF1E2",
      secondary: "#945800",
      tertiary: "#ff9800",
      white: "#FFFFFF",
      black: "#000000",
    },
    text: {
      primary: "#C9A777", // Primary text color
      secondary: "#7B6E66", // Secondary text color
      tertiary: "#796E67",
    },
    button: {
      primary: "#C9A777",
      secondary: "#7B6E66",
      disabled: "#BC955C",
      hover: "#b4966b",
    },
    menuItem: {
      primary: "#f5f5f5",
      secondary: "#e0e0e0",
    },
  },
  components: {
    MuiTextField: {
      defaultProps: {
        autoComplete: "off",
        inputProps: { autoComplete: "off" },
      },
    },
    MuiInputBase: {
      defaultProps: {
        autoComplete: "off",
      },
    },
    MuiInput: {
      defaultProps: {
        autoComplete: "off",
      },
    },
    // Global baseline so every outlined input renders with the same
    // white background, gold focus ring and rounded corners — even when
    // a screen uses TextField / Select directly without going through
    // the shared input components.
    MuiOutlinedInput: {
      defaultProps: {
        autoComplete: "off",
      },
      styleOverrides: {
        root: {
          backgroundColor: "#fff",
          borderRadius: 10,
          transition: "box-shadow .2s ease, border-color .2s ease",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(123,110,102,0.20)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(201,167,119,0.55)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#C9A777",
            borderWidth: 1.5,
          },
          "&.Mui-focused": {
            boxShadow: "0 0 0 4px rgba(201,167,119,0.15)",
          },
          "&.Mui-disabled": {
            backgroundColor: "#fdf6ea",
          },
        },
        // Standardized vertical padding so inline TextFields and shared
        // TextBoxComponent inputs have identical height across all forms.
        input: {
          color: "#3d322b",
          fontSize: "0.9375rem",
          paddingTop: "11.2px",
          paddingBottom: "11.2px",
          "&::placeholder": {
            color: "#9c8e85",
            opacity: 1,
          },
        },
      },
    },
    // Match Select padding to OutlinedInput so Select height aligns with TextField.
    MuiSelect: {
      styleOverrides: {
        select: {
          paddingTop: "11.2px",
          paddingBottom: "11.2px",
          fontSize: "0.9375rem",
          color: "#3d322b",
        },
        icon: {
          color: "#a1885e",
        },
      },
    },
    // Branded dropdown menu (paper + items) so every <Select> opens with the
    // same look — rounded corners, cream-shadowed paper, gold-tinted hover/
    // selected states. Inline Selects get this for free; shared Select.js
    // no longer needs custom MenuProps.
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 8,
          borderRadius: 10,
          border: "1px solid rgba(123,110,102,0.10)",
          boxShadow: "0 18px 40px rgba(123,110,102,0.18)",
        },
        list: {
          padding: 4,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: 9,
          paddingBottom: 9,
          fontSize: "0.9375rem",
          color: "#3d322b",
          borderRadius: 6,
          margin: "1px 0",
          "&:hover": {
            backgroundColor: "#fdf6ea",
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(201,167,119,0.18)",
            "&:hover": {
              backgroundColor: "rgba(201,167,119,0.25)",
            },
          },
          "&.Mui-disabled": {
            color: "#9c8e85",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#7B6E66",
          "&.Mui-focused": { color: "#a1885e" },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: "0.8125rem",
          letterSpacing: 0.1,
          "&.Mui-error": {
            color: "#d4575a",
          },
        },
      },
    },
    // Brand-color radios and checkboxes for inline RadioGroup / Checkbox usage
    // so they match the shared Radio.js / CheckBox.js components.
    MuiRadio: {
      styleOverrides: {
        root: {
          color: "rgba(123,110,102,0.55)",
          "&.Mui-checked": { color: "#C9A777" },
        },
      },
    },
    // Deterministic gap between side-by-side radios (`<RadioGroup row>`):
    // FormControlLabel defaults to marginRight: 16px which makes the visible
    // distance between options vary with label width. Resetting that and
    // using flex `gap` instead means every pair of radios sits exactly 24px
    // apart, regardless of the label text.
    MuiRadioGroup: {
      styleOverrides: {
        row: {
          gap: 24,
          "& .MuiFormControlLabel-root": {
            marginRight: 0,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "rgba(123,110,102,0.55)",
          "&.Mui-checked": { color: "#C9A777" },
        },
      },
    },
  },
});

export default theme;
